import { useContext, useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import { Icon as BlueIcon } from '@blueprintjs/core';
import { ClickAwayListener } from '@material-ui/core';

import { AssetContext } from '@pages/Asset/Asset.query';

import { IEquipmentStatus } from '@models/EquipmentStatus.model';

import { useMutation, useQueryClient } from 'react-query';
import { patchEquipmentStatus } from '@services/queries/equipments';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { getPermissionData } from '@utils';
import { NavigationContext } from '@layout/navigation/navigation';
import ReactTooltip from 'react-tooltip';

import { trackStatusChanged } from '@services/mixpanel/trackers/AssetMetrics';
import {
  Container,
  ArrowDown,
  Right,
  StatusText,
  Text,
  StatusIcon,
  LineOverflow,
  Line,
  Divider,
  Block,
  BlockText,
  Label,
  TextArea,
  Buttons,
  Cancel,
  Done,
  Comment,
  Avatar,
  LastChanged,
  LastChangedDate,
  LastChangedText,
  Empty,
  StatusBadge,
} from '../../../../../../components/Status/AssetStatus.styles';
import { StatusList } from './components';

export const Status = () => {
  const params = useParams<{ equipmentId: string }>();
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [newStatus, setNewStatus] = useState<IEquipmentStatus | null>(null);

  const { role, studioName } = useContext(NavigationContext);
  const { equipmentQuery, statusAuthor } = useContext(AssetContext);

  const equipment = equipmentQuery?.data;

  const queryClient = useQueryClient();

  const mutationPatchEquipment = useMutation(patchEquipmentStatus, {
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries(['equipments', params.equipmentId]);
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'statusAuthor',
      ]);
      setOpen(false);
      if (newStatus) {
        const { name, category } = newStatus;
        const includedNote = Number(variables.data.statusComment?.length) > 0;
        trackStatusChanged(studioName, name, category, includedNote);
      }
    },
  });

  if (!equipment) {
    return <div />;
  }

  const statusPermission = getPermissionData({
    action: 'status',
    owner: false,
    role,
    type: '',
  });

  const cancel = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    if (statusPermission.allowed) {
      setOpen(true);
      setNewStatus(null);
      setComment('');
    }
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Container>
        <Line
          onClick={handleOpen}
          data-tip={statusPermission.message}
          data-for="statusChange"
        >
          <Text>Status</Text>
          {equipment.equipmentStatus ? (
            <Right>
              <StatusBadge category={equipment.equipmentStatus.category} />
              <StatusText>{equipment.equipmentStatus?.name}</StatusText>
              <ArrowDown>
                <BlueIcon icon="chevron-down" color="#8F95B2" />
              </ArrowDown>
            </Right>
          ) : (
            <Empty>Empty</Empty>
          )}
        </Line>
        <ReactTooltip
          id="statusChange"
          className="tooltip"
          effect="solid"
          multiline
          disable={statusPermission.allowed}
        />

        <CSSTransition
          in={open}
          timeout={300}
          classNames="my-node"
          unmountOnExit
          onEnter={() => setOpen(true)}
          onExited={cancel}
        >
          <Block>
            {newStatus ? (
              <>
                <LineOverflow onClick={cancel}>
                  <Text>Setting As</Text>
                  <Right>
                    <StatusBadge category={newStatus.category} />
                    <StatusText>{newStatus.name}</StatusText>
                  </Right>
                </LineOverflow>
                <Divider />
                <Label>Status change comments (Optional)</Label>
                <TextArea
                  placeholder="Placeholder"
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                />

                <Buttons>
                  <Cancel onClick={cancel} type="button">
                    Cancel
                  </Cancel>
                  <Done
                    type="button"
                    disabled={mutationPatchEquipment.isLoading}
                    onClick={() => {
                      mutationPatchEquipment.mutate({
                        equipmentId: params.equipmentId,
                        data: {
                          equipmentStatusId: newStatus.id,
                          statusComment: comment,
                        },
                      });
                    }}
                  >
                    Change
                  </Done>
                </Buttons>
              </>
            ) : (
              <>
                <LineOverflow onClick={cancel}>
                  <Text>Status</Text>
                  {equipment.equipmentStatus && (
                    <Right>
                      <StatusIcon>
                        <StatusBadge
                          category={equipment.equipmentStatus.category}
                        />
                      </StatusIcon>
                      <StatusText>{equipment.equipmentStatus?.name}</StatusText>
                    </Right>
                  )}
                </LineOverflow>
                {equipment.statusComment && (
                  <Comment>{equipment.statusComment}</Comment>
                )}

                {(equipment.statusUser || equipment.statusUpdatedAt) &&
                  statusAuthor?.data && (
                    <LastChanged>
                      <LastChangedText>Last changed by:</LastChangedText>
                      {equipment.statusUser && statusAuthor?.data && (
                        <Avatar
                          src={statusAuthor.data.picture}
                          alt={equipment.statusUser.name}
                        />
                      )}
                      {equipment.statusUpdatedAt && (
                        <LastChangedDate>
                          {format(equipment.statusUpdatedAt, 'MM/dd/yyyy')}
                        </LastChangedDate>
                      )}
                    </LastChanged>
                  )}

                <Divider />
                <BlockText>Set As</BlockText>
                <StatusList setNewStatus={setNewStatus} />
              </>
            )}
          </Block>
        </CSSTransition>
      </Container>
    </ClickAwayListener>
  );
};
